import React, {useCallback, useMemo} from "react";
import {Manager, Reference, Popper} from "react-popper";
import {Icon, Toggle, theme} from "@reside/ui";
import {DataNode} from "rc-tree/lib/interface";
import {TreeSelect} from "../../../../atoms/tree-select/TreeSelect";
import {DropdownMenu} from "../../../../atoms/dropdown-tree-select/styles";
import {
  FilterPillBadge,
  FilterPillChevron,
  FilterPillLabel,
  FilterPillTrigger,
} from "./permissionsManagementFilters.styles";

type Props = Readonly<{
  triggerLabel: string;
  checkedKeys: ReadonlyArray<string>;
  treeData: ReadonlyArray<DataNode & {className?: string}>;
  onCheck: (checkedKeys: string[], checkedLeafKeys: string[]) => void;
  withApplyFiltersButton?: boolean;
}>;

export const PermissionsManagementFilterDropdown = ({
  triggerLabel,
  checkedKeys,
  treeData,
  onCheck,
  withApplyFiltersButton = true,
}: Props) => {
  const leafs = useMemo(
    () => treeData.reduce<DataNode[]>((keys, node) => [...keys, ...node.children], []),
    [treeData],
  );

  const checkedLeafKeys = useMemo(
    () =>
      checkedKeys.filter(
        key => !treeData.find(node => node.key === key),
      ),
    [checkedKeys, treeData],
  );

  const selectedCount = checkedLeafKeys.length;
  const isActive = selectedCount > 0;

  const handleCheck = useCallback(
    (newCheckedKeys: string[], newCheckedLeafKeys: string[]) => {
      onCheck(newCheckedKeys, newCheckedLeafKeys);
    },
    [onCheck],
  );

  return (
    <Toggle>
      {({isOpened, setIsOpened, outsideClickIgnoreClass}) => (
        <Manager>
          <Reference>
            {({ref}) => (
              <FilterPillTrigger
                ref={ref}
                $active={isActive}
                type="button"
                onClick={() => setIsOpened(!isOpened)}
              >
                <FilterPillLabel $active={isActive}>{triggerLabel}</FilterPillLabel>
                {isActive && <FilterPillBadge>{selectedCount}</FilterPillBadge>}
                <FilterPillChevron>
                  <Icon
                    name={isOpened ? "up" : "down"}
                    size={12}
                    color={isActive ? theme.color.white : theme.color.darkBlue100}
                  />
                </FilterPillChevron>
              </FilterPillTrigger>
            )}
          </Reference>
          {isOpened && (
            <Popper placement="bottom">
              {({ref, style}) => (
                <DropdownMenu
                  ref={ref}
                  style={style}
                  className={outsideClickIgnoreClass}
                >
                  <TreeSelect
                    treeData={treeData}
                    checkedKeys={checkedKeys}
                    onCheck={handleCheck}
                    withSearch={false}
                    withApplyFiltersButton={withApplyFiltersButton}
                  />
                </DropdownMenu>
              )}
            </Popper>
          )}
        </Manager>
      )}
    </Toggle>
  );
};
